/**
 * SEIRV — SEIRD with leaky vaccination.
 *
 *   dS/dt = -β·S·I/N - v·S
 *   dE/dt =  β·S·I/N + (1-VE)·β·V·I/N - σ·E
 *   dI/dt =  σ·E - γ·I
 *   dR/dt =  γ·(1-IFR)·I
 *   dD/dt =  γ·IFR·I
 *   dV/dt =  v·S - waning·V - (1-VE)·β·V·I/N
 *
 * v(t) = vaccination rate per susceptible per day (constant in v0.1).
 * VE in [0,1]: 1.0 = sterilising; 0.0 = no protection.
 * Waning: optional exponential decay of vaccinated → susceptible.
 *
 * Limit: VE=1, waning=0 → vaccinated never become infected.
 *        VE=0          → SEIRV reduces to SEIRD (vaccination has no effect).
 */

import { integrateRk4 } from './rk4.js';
import { ENGINE_VERSION } from './sir.js';
import type { SeirvParams, EpiResult, BetaScaler } from './types.js';

export interface SeirvOptions {
  betaScaler?: BetaScaler;
}

export function simulateSeirv(params: SeirvParams, opts: SeirvOptions = {}): EpiResult {
  const N = params.N;
  const I0 = params.I0;
  const E0 = params.E0 ?? 0;
  const R0Pop = params.R0Pop ?? 0;
  const V0 = params.V0 ?? 0;
  const S0 = N - I0 - E0 - R0Pop - V0;
  const dt = params.dtDays ?? 0.5;
  const beta = params.beta;
  const gamma = params.gamma;
  const sigma = params.sigma;
  const ifr = params.ifr;
  const v = params.v;
  const ve = params.ve;
  const waning = params.waningHalfLifeDays !== undefined && params.waningHalfLifeDays > 0
    ? Math.log(2) / params.waningHalfLifeDays
    : 0;
  const scaler = opts.betaScaler;
  const t0 = performance.now();

  if (S0 < 0) throw new Error(`SEIRV: N < I0+E0+R0Pop+V0`);
  if (ve < 0 || ve > 1) throw new Error(`SEIRV: ve (${ve}) must be in [0,1]`);
  if (ifr < 0 || ifr > 1) throw new Error(`SEIRV: ifr must be in [0,1]`);
  if (v < 0) throw new Error(`SEIRV: vaccination rate must be non-negative`);

  const f = (t: number, y: number[]): number[] => {
    const S = y[0]!, E = y[1]!, I = y[2]!, V = y[5]!;
    const b = scaler ? beta * scaler(t) : beta;
    const newFromS = (b * S * I) / N;
    const newFromV = (1 - ve) * (b * V * I) / N;
    const becomeInfectious = sigma * E;
    const outcomes = gamma * I;
    const vaccinations = v * S;
    const wane = waning * V;
    return [
      -newFromS - vaccinations + wane,           // S
      newFromS + newFromV - becomeInfectious,    // E
      becomeInfectious - outcomes,                // I
      outcomes * (1 - ifr),                       // R
      outcomes * ifr,                             // D
      vaccinations - newFromV - wane,             // V
    ];
  };

  const integrated = integrateRk4(f, [S0, E0, I0, R0Pop, 0, V0], params.durationDays, dt);
  const compartments: Record<string, number[]> = { S: [], E: [], I: [], R: [], D: [], V: [] };
  for (const row of integrated.y) {
    compartments.S!.push(row[0]!);
    compartments.E!.push(row[1]!);
    compartments.I!.push(row[2]!);
    compartments.R!.push(row[3]!);
    compartments.D!.push(row[4]!);
    compartments.V!.push(row[5]!);
  }

  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: 'seirv',
      elapsedMs: performance.now() - t0,
      steps: integrated.steps,
    },
  };
}
